import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import truemoneyLogo from '@/assets/truemoney-logo.png'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import './ModernTopupPage.css'
import {
  getDepositChannels,
  getDepositPresetAmounts,
  syncPresetAmountsAfterDepositChannelsUpdate,
  getPublicPromptPayAccounts,
  getPublicDepositAccounts,
  isTrueMoneyDepositAccount,
  mapPromptPayAccountsToDepositAccounts,
  resolveDepositAccountForAmount,
  type DepositChannels,
  type PublicDepositAccount,
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import { pendingDepositDraftService, type PendingDepositMethod } from '../services/pendingDepositDraftService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernTopupPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

interface TopupTab {
  id: number
  label: string
  method: PendingDepositMethod
  kind: 'payment' | 'bank' | 'true_money' | 'prompt_pay' | 'decimal'
}

const TRUEMONEY_LOGO_URL = truemoneyLogo

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const createDraftId = () => `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const buildSenderAccountSnapshot = (account?: authService.UserBankAccount | null) => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const ModernTopupPage: React.FC<ModernTopupPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [transferAmount, setTransferAmount] = useState('')
  const [decimalAmount, setDecimalAmount] = useState('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [promptPayAccounts, setPromptPayAccounts] = useState<PublicDepositAccount[]>([])
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [bankCheckDone, setBankCheckDone] = useState(false)
  const [depositChannels, setDepositChannels] = useState<DepositChannels>({
    bank: true,
    trueMoney: true,
    promptPay: true,
    decimalReservation: true,
    allowSubmitWithoutSlip: true,
  })
  const [presetAmounts, setPresetAmounts] = useState<number[]>([])

  const applyDepositChannels = (channels?: Partial<DepositChannels> | null) => {
    if (!channels || typeof channels !== 'object') return false
    setDepositChannels({
      bank: channels.bank !== false,
      trueMoney: channels.trueMoney !== false,
      promptPay: channels.promptPay !== false,
      decimalReservation: channels.decimalReservation !== false,
      allowSubmitWithoutSlip: channels.allowSubmitWithoutSlip !== false,
    })
    return true
  }

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
      authService.fetchUserBankAccounts(),
    ]).then(([walletRes, summary, bankAccounts]) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
      const nextBankAccounts = Array.isArray(bankAccounts) ? bankAccounts : []
      setUserBankAccounts(nextBankAccounts)
      setBankCheckDone(true)
      if (nextBankAccounts.length === 0) navigate('/member', { replace: true })
    })
  }, [navigate])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId) return

    Promise.all([
      getPublicDepositAccounts(tenantId),
      getPublicPromptPayAccounts(tenantId),
      getDepositChannels(tenantId),
      getDepositPresetAmounts(tenantId),
      getDepositFlowConfig(tenantId),
    ]).then(([accounts, promptPayList, channels, presetAmountsResponse, flow]) => {
      setDepositAccounts(accounts)
      setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))
      setDepositChannels(channels)
      setPresetAmounts(presetAmountsResponse)
      setDepositFlow(flow)
    })
  }, [themeConfig.tenantId])

  useEffect(() => {
    const tenantId = themeConfig.tenantId
    if (!tenantId) return

    const onDepositChannelsUpdated = (
      event: CustomEvent<{
        tenantId?: string
        content?: {
          depositChannels?: Partial<DepositChannels>
          settings?: { depositChannels?: Partial<DepositChannels> }
        }
      }>
    ) => {
      if (event.detail?.tenantId !== tenantId) return
      const contentChannels = event.detail?.content?.depositChannels || event.detail?.content?.settings?.depositChannels
      if (!applyDepositChannels(contentChannels)) {
        getDepositChannels(tenantId, { forceRefresh: true }).then(setDepositChannels)
      }
      void syncPresetAmountsAfterDepositChannelsUpdate(tenantId, contentChannels).then(setPresetAmounts)
      getDepositFlowConfig(tenantId).then(setDepositFlow)
    }

    const onDepositAccountsUpdated = (event: CustomEvent<{ tenantId?: string }>) => {
      if (event.detail?.tenantId !== tenantId) return
      Promise.all([
        getPublicDepositAccounts(tenantId),
        getPublicPromptPayAccounts(tenantId),
      ]).then(([accounts, promptPayList]) => {
        setDepositAccounts(accounts)
        setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))
      })
    }

    window.addEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
    window.addEventListener('deposit_accounts_updated', onDepositAccountsUpdated as EventListener)
    return () => {
      window.removeEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
      window.removeEventListener('deposit_accounts_updated', onDepositAccountsUpdated as EventListener)
    }
  }, [themeConfig.tenantId])

  const defaultBankAccount = useMemo(
    () =>
      depositAccounts.find((account) => !isTrueMoneyDepositAccount(account) && account.isDefault) ??
      depositAccounts.find((account) => !isTrueMoneyDepositAccount(account)) ??
      null,
    [depositAccounts]
  )
  const enteredTransferAmount = useMemo(() => {
    const amount = parseFloat(transferAmount.trim())
    return Number.isFinite(amount) ? amount : NaN
  }, [transferAmount])
  const selectedBankAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredTransferAmount, { channel: 'bank' }) ?? defaultBankAccount,
    [defaultBankAccount, depositAccounts, enteredTransferAmount]
  )
  const selectedTrueMoneyAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredTransferAmount, { channel: 'true_money' }),
    [depositAccounts, enteredTransferAmount]
  )
  const selectedPromptPayAccount = useMemo(
    () => resolveDepositAccountForAmount(promptPayAccounts, enteredTransferAmount, { channel: 'promptpay' }),
    [enteredTransferAmount, promptPayAccounts]
  )
  const enteredDecimalAmount = useMemo(() => {
    const amount = parseFloat(decimalAmount.trim())
    return Number.isFinite(amount) ? amount : NaN
  }, [decimalAmount])
  const selectedDecimalReceiverAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredDecimalAmount, { channel: 'bank' }) ?? defaultBankAccount,
    [defaultBankAccount, depositAccounts, enteredDecimalAmount]
  )
  const selectedUserBankAccount = userBankAccounts[0] ?? null

  const availableTabs = useMemo<TopupTab[]>(() => {
    const tabs: TopupTab[] = []
    if (depositChannels.bank) {
      tabs.push({
        id: 0,
        label: 'ธนาคาร',
        method: 'bank_transfer',
        kind: 'bank',
      })
    }
    if (depositChannels.trueMoney && depositAccounts.some((account) => isTrueMoneyDepositAccount(account))) {
      tabs.push({
        id: 1,
        label: 'TrueMoney',
        method: 'true_money',
        kind: 'true_money',
      })
    }
    if (depositChannels.promptPay && promptPayAccounts.length > 0) {
      tabs.push({
        id: 2,
        label: 'PromptPay',
        method: 'prompt_pay',
        kind: 'prompt_pay',
      })
    }
    if (depositChannels.decimalReservation) {
      tabs.push({
        id: 3,
        label: 'ฝากทศนิยม',
        method: 'decimal_reservation',
        kind: 'decimal',
      })
    }
    return tabs
  }, [depositAccounts, depositChannels, promptPayAccounts.length])

  useEffect(() => {
    if (availableTabs.some((tab) => tab.id === activeTab)) return
    if (availableTabs[0]) setActiveTab(availableTabs[0].id)
  }, [activeTab, availableTabs])

  const activeTabConfig = useMemo(
    () => availableTabs.find((tab) => tab.id === activeTab) ?? availableTabs[0] ?? null,
    [activeTab, availableTabs]
  )
  const activeMethod = activeTabConfig?.method ?? 'bank_transfer'
  const methodGridClass =
    availableTabs.length <= 1
      ? 'htd-topup__method-grid--1'
      : availableTabs.length === 2
        ? 'htd-topup__method-grid--2'
        : availableTabs.length === 3
          ? 'htd-topup__method-grid--3'
          : 'htd-topup__method-grid--4'
  const selectedAccount = useMemo(() => {
    if (activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled) return null
    if (activeMethod === 'true_money') return selectedTrueMoneyAccount
    if (activeMethod === 'prompt_pay') return selectedPromptPayAccount
    if (activeMethod === 'bank_transfer') return selectedBankAccount ?? defaultBankAccount
    return null
  }, [activeMethod, defaultBankAccount, depositFlow.paymentGatewayEnabled, selectedBankAccount, selectedPromptPayAccount, selectedTrueMoneyAccount])
  const senderAccountSnapshot = useMemo(
    () => buildSenderAccountSnapshot(selectedUserBankAccount),
    [selectedUserBankAccount]
  )

  const balanceText =
    walletData != null
      ? `${walletService.formatCurrency(walletService.getDisplayBalance(walletData))} ฿`
      : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const userBankSummaryText = selectedUserBankAccount
    ? `${selectedUserBankAccount.accountNumber} | ${selectedUserBankAccount.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'
  const displayedPresetAmounts = presetAmounts
  const selectedPresetAmount = useMemo(
    () => displayedPresetAmounts.find((amount) => String(amount) === transferAmount.trim()) ?? null,
    [displayedPresetAmounts, transferAmount]
  )
  const selectedDecimalPresetAmount = useMemo(
    () => displayedPresetAmounts.find((amount) => String(amount) === decimalAmount.trim()) ?? null,
    [decimalAmount, displayedPresetAmounts]
  )
  const canContinue =
    activeMethod === 'decimal_reservation'
      ? Boolean(decimalAmount.trim()) && parseFloat(decimalAmount) > 0
      : Boolean(transferAmount.trim()) && parseFloat(transferAmount) > 0 && ((activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled) || Boolean(selectedAccount))

  const goToSummary = () => {
    const tenantId = themeConfig.tenantId
    const currentUser = authService.getCurrentUser()
    if (!tenantId || !currentUser?.id) return

    if (activeMethod === 'decimal_reservation') {
      const amount = decimalAmount.trim()
      if (!amount || !/^\d+$/.test(amount) || parseFloat(amount) <= 0) return
      navigate('/topup/reserve', {
        state: {
          amount,
          depositAccount: selectedDecimalReceiverAccount,
          senderAccount: senderAccountSnapshot,
        },
      })
      return
    }

    const amountValue = transferAmount.trim()
    if (!amountValue || !/^\d*\.?\d*$/.test(amountValue) || parseFloat(amountValue) <= 0) return

    const draftId = createDraftId()
    const nextAmount = String(Math.floor(parseFloat(amountValue)))
    const usesPaymentGateway = activeMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled
    const fallbackBankAccount =
      activeMethod === 'bank_transfer'
        ? (selectedBankAccount ?? defaultBankAccount ?? undefined)
        : undefined
    const nextDepositAccount = activeMethod === 'bank_transfer'
      ? fallbackBankAccount
      : selectedAccount ?? undefined

    pendingDepositDraftService.upsert(tenantId, currentUser.id, {
      id: draftId,
      amount: nextAmount,
      depositMethod: activeMethod,
      depositAccount: nextDepositAccount,
      senderAccount: senderAccountSnapshot,
      usesPaymentGateway,
    })

    const navigationState = {
      amount: nextAmount,
      depositAccount: nextDepositAccount,
      depositMethod: activeMethod,
      senderAccount: senderAccountSnapshot,
      usesPaymentGateway,
    }
    const shouldSkipConfirmPage = !usesPaymentGateway && activeMethod !== 'prompt_pay'

    navigate(
      shouldSkipConfirmPage
        ? `/topup/slip?draft=${encodeURIComponent(draftId)}`
        : `/topup/confirm?draft=${encodeURIComponent(draftId)}`,
      { state: navigationState }
    )
  }

  const renderTabContent = (tab: TopupTab) => {
    if (tab.kind === 'payment') {
      return (
        <span className="d-inline-flex align-items-center gap-2">
          <i className="fa fa-qrcode" />
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'true_money') {
      return (
        <span className="topup-method-btn__truemoney">
          <img
            src={selectedTrueMoneyAccount?.bankIcon || TRUEMONEY_LOGO_URL}
            alt=""
            aria-hidden="true"
            className="topup-method-btn__truemoney-logo"
            onError={(event) => {
              const element = event.target as HTMLImageElement
              if (element.src !== TRUEMONEY_LOGO_URL) element.src = TRUEMONEY_LOGO_URL
            }}
          />
          <span className="topup-method-btn__truemoney-text">TrueMoney</span>
        </span>
      )
    }

    if (tab.kind === 'bank') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-university" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'decimal') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-coins" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    if (tab.kind === 'prompt_pay') {
      return (
        <span className="topup-method-btn__label">
          <span className="topup-method-btn__icon" aria-hidden="true">
            <i className="fas fa-qrcode" />
          </span>
          <span>{tab.label}</span>
        </span>
      )
    }

    return <span>{tab.label}</span>
  }

  const renderAmountButton = (
    amount: number,
    selected: boolean,
    onClick: () => void,
    badgeText: string
  ) => (
    <button
      type="button"
      className={`htd-topup__preset${selected ? ' htd-topup__preset--active' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${badgeText} ${amount.toLocaleString()} บาท`}
    >
      <span className="htd-topup__preset-badge">{selected ? 'เลือกแล้ว' : badgeText}</span>
      <span className="htd-topup__preset-amount">{amount.toLocaleString()}</span>
      <span className="htd-topup__preset-unit">บาท</span>
    </button>
  )

  const depositEnabled = themeConfig.settings?.depositEnabled !== false

  if (!bankCheckDone) {
    return (
      <div className="content htd-topup">
        <div className="htd-topup__glow" aria-hidden />
        <div className="htd-topup__loading">กำลังโหลด...</div>
      </div>
    )
  }

  if (!depositEnabled) {
    return (
      <div className="content htd-topup">
        <div className="htd-topup__glow" aria-hidden />
        <div className="htd-topup__disabled">
          <span className="htd-topup__disabled-icon" aria-hidden>
            <i className="fas fa-ban" />
          </span>
          <h2>ปิดการฝากเงินชั่วคราว</h2>
          <p>ขณะนี้ระบบปิดการรับฝากเงิน กรุณาติดต่อแอดมินหรือลองใหม่ภายหลัง</p>
        </div>
      </div>
    )
  }

  return (
    <div className="content htd-topup">
      <div className="htd-topup__glow" aria-hidden />
      <div className="htd-topup__inner">
        <header className="htd-topup__hero htd-topup-anim htd-topup-anim--1">
          <div className="htd-topup__badge" aria-hidden>
            <i className="fas fa-wallet" />
          </div>
          <div>
            <p className="htd-topup__eyebrow">Deposit</p>
            <h1 className="htd-topup__title">ฝากเงิน</h1>
            <p className="htd-topup__desc">เลือกช่องทาง ระบุยอด แล้วไปขั้นตอนยืนยัน / อัปโหลดสลิป</p>
          </div>
        </header>

        <section className="htd-topup__stats htd-topup-anim htd-topup-anim--2" aria-label="สรุปบัญชี">
          <div className="htd-topup__stat">
            <span className="htd-topup__stat-label">ยอดเครดิต</span>
            <span className="htd-topup__stat-value">{balanceText}</span>
          </div>
          <div className="htd-topup__stat">
            <span className="htd-topup__stat-label">คะแนน</span>
            <span className="htd-topup__stat-value">{pointsText}</span>
            <Link to="/points" className="htd-topup__stat-link">
              ดูคะแนนทั้งหมด
            </Link>
          </div>
          <div className="htd-topup__stat htd-topup__stat--bank">
            <span className="htd-topup__stat-label">บัญชีของคุณ</span>
            <span className="htd-topup__stat-value">{userBankSummaryText}</span>
          </div>
        </section>

        <div className="htd-topup__actions htd-topup-anim htd-topup-anim--2">
          <span className="htd-topup__action htd-topup__action--deposit">
            <i className="fas fa-arrow-down" aria-hidden />
            ฝากเงิน
          </span>
          <Link to="/withdraw" className="htd-topup__action htd-topup__action--withdraw">
            <i className="fas fa-arrow-up" aria-hidden />
            ถอนเงิน
          </Link>
        </div>

        <section className="htd-topup__panel htd-topup__panel--bright htd-topup-anim htd-topup-anim--3" aria-label="เลือกวิธีการฝาก">
          <p className="htd-topup__panel-label">เลือกวิธีการฝาก</p>
          <div className={`htd-topup__method-grid ${methodGridClass}`}>
            {availableTabs.map((tab) => {
              const selected = activeTabConfig?.id === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`topup-method-btn${selected ? ' topup-method-btn--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  aria-pressed={selected}
                  aria-label={tab.label}
                >
                  <span className="topup-method-btn__content">{renderTabContent(tab)}</span>
                </button>
              )
            })}
          </div>
        </section>

        <section className="htd-topup__panel htd-topup__panel--bright htd-topup-anim htd-topup-anim--4" aria-label="ระบุยอดฝาก">
          {activeMethod !== 'decimal_reservation' ? (
            <>
              <div className="htd-topup__amount-panel">
                {displayedPresetAmounts.length > 0 && (
                  <>
                    <div className="htd-topup__amount-head">
                      <div>
                        <p className="htd-topup__panel-label" style={{ marginBottom: 0 }}>
                          เลือกยอดด่วน
                        </p>
                        <p className="htd-topup__amount-head-title">แตะ 1 ครั้งเพื่อเลือกยอดที่ต้องการฝาก</p>
                      </div>
                      {selectedPresetAmount != null && (
                        <span className="htd-topup__chip">
                          เลือกไว้ {selectedPresetAmount.toLocaleString()} บาท
                        </span>
                      )}
                    </div>
                    <div className="htd-topup__preset-grid">
                      {displayedPresetAmounts.map((amount) => (
                        <div key={`${activeMethod}-${amount}`}>
                          {renderAmountButton(
                            amount,
                            selectedPresetAmount === amount,
                            () => setTransferAmount(String(amount)),
                            'ยอดด่วน'
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="htd-topup__input-wrap">
                  <p className="htd-topup__panel-label">ระบุจำนวนเงินเอง</p>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="0.01"
                    placeholder="กรอกจำนวนเงินที่ต้องการฝาก"
                    value={transferAmount}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '' || /^\d*\.?\d*$/.test(value)) setTransferAmount(value)
                    }}
                    className="htd-topup__input"
                    aria-label="จำนวนเงินที่ต้องการฝาก"
                  />
                  <p className="htd-topup__input-hint">
                    {displayedPresetAmounts.length > 0
                      ? 'กดเลือกยอดด้านบน หรือพิมพ์จำนวนเงินเองได้ทันที'
                      : 'พิมพ์จำนวนเงินที่ต้องการฝาก'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="htd-topup__submit"
                disabled={!canContinue}
                onClick={goToSummary}
              >
                <i className="fas fa-check-circle" aria-hidden />
                ยืนยันยอดและดูรายละเอียด
              </button>
            </>
          ) : (
            <>
              <div className="htd-topup__amount-panel">
                <p className="htd-topup__decimal-title">ใส่จำนวนเงินเต็มที่ต้องการเติม/ฝาก</p>
                {displayedPresetAmounts.length > 0 && (
                  <>
                    <div className="htd-topup__amount-head">
                      <div>
                        <p className="htd-topup__panel-label" style={{ marginBottom: 0 }}>
                          ยอดที่นิยม
                        </p>
                        <p className="htd-topup__amount-head-title">เลือกยอดเต็มก่อนจองทศนิยม</p>
                      </div>
                      {selectedDecimalPresetAmount != null && (
                        <span className="htd-topup__chip">
                          เลือกไว้ {selectedDecimalPresetAmount.toLocaleString()} บาท
                        </span>
                      )}
                    </div>
                    <div className="htd-topup__preset-grid">
                      {displayedPresetAmounts.map((amount) => (
                        <div key={`decimal-${amount}`}>
                          {renderAmountButton(
                            amount,
                            selectedDecimalPresetAmount === amount,
                            () => setDecimalAmount(String(amount)),
                            'ยอดแนะนำ'
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="htd-topup__input-wrap">
                  <p className="htd-topup__panel-label">ระบุจำนวนเงินเอง</p>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    step="1"
                    placeholder="กรอกจำนวนเงิน"
                    value={decimalAmount}
                    onChange={(event) => {
                      const value = event.target.value
                      if (value === '' || /^\d*$/.test(value)) setDecimalAmount(value)
                    }}
                    className="htd-topup__input"
                    aria-label="จำนวนเงินฝากทศนิยม"
                  />
                  <p className="htd-topup__input-hint">พิมพ์ยอดที่ต้องการจองได้เลย</p>
                </div>
              </div>

              <button
                type="button"
                className="htd-topup__submit"
                disabled={!canContinue}
                onClick={goToSummary}
              >
                <i className="fas fa-clock" aria-hidden />
                จองการโอนเงินผ่านระบบ
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

export default ModernTopupPage
